import React from 'react';
import { Flex, Box, Divider } from '@chakra-ui/react';
import { KText, KTag } from '@/ui-library/components';
import moment from 'moment';
import { colors } from '@/theme/foundations/colors';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { sanitizeHtml } from '@/lib/purify';

interface SurveyPreviewWithAnswersProps {
  customFields: any[];
  fieldGroups?: Record<number, string>;
}

const renderTagValue = (value: string | number, size: string = 'sm-medium') => (
  <KTag
    value={String(value)}
    size={size}
    bgColor="secondary-50"
    color="secondary-700"
    w="fit-content"
  />
);

const renderTextValue = (value: string) => (
  <Box
    px="12px"
    py="4px"
    bg="secondary-50"
    borderRadius="8px"
    dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
    w="fit-content"
    fontSize="14px"
    fontWeight="500"
    color="secondary-700"
  />
);

const renderRelTagMultiSelect = (
  values?: Array<{ id: number | string; value: string }>
) => {
  if (!values || values.length === 0) {
    return null;
  }

  // Show all values for multi-select
  return (
    <Flex gap="8px" flexWrap="wrap">
      {values.map((val) => (
        <KTag
          key={val.id}
          value={val.value}
          size="sm-medium"
          bgColor="secondary-50"
          color="secondary-700"
          w="fit-content"
        />
      ))}
    </Flex>
  );
};

const renderRelTagSingleSelect = (
  values?: Array<{ id: number | string; value: string }>
) => {
  if (!values || values.length === 0) {
    return null;
  }
  // Show all values as separate tags for single select
  return (
    <Flex gap="8px" flexWrap="wrap">
      {values.map((val) => (
        <KTag
          key={val.id}
          value={val.value}
          size="sm-medium"
          bgColor="secondary-50"
          color="secondary-700"
          w="fit-content"
        />
      ))}
    </Flex>
  );
};

const renderBooleanValue = () => (
  <Flex gap="6px" flexWrap="wrap">
    <KTag
      value="Yes"
      size="sm-medium"
      bgColor="secondary-50"
      color="secondary-700"
      w="fit-content"
    />
    <KTag
      value="No"
      size="sm-medium"
      bgColor="secondary-50"
      color="secondary-700"
      w="fit-content"
    />
  </Flex>
);

const renderDateValue = () => {
  const formattedDate = moment().format('MMMM D, YYYY');
  return renderTagValue(formattedDate);
};

const renderFileUpload = () => {
  return (
    <Flex
      alignItems="center"
      gap="8px"
      px="12px"
      py="8px"
      bg="secondary-50"
      borderRadius="8px"
      w="fit-content"
    >
      <KText size="sm-medium" color="secondary-700">
        example-document.pdf
      </KText>
    </Flex>
  );
};

const getExampleValue = (field: any): React.ReactNode => {
  const { kind, values } = field;

  switch (kind) {
    case 'nps_rating':
      return renderTagValue('0 - 10');

    case 'star_rating':
      return renderTagValue('1 - 5');

    case 'scale_rating':
      return renderTagValue('0 - 10', 'sm-semi-bold');

    case 'string':
      return renderTagValue('Sample answer');

    case 'text':
      return renderTextValue('This is a sample detailed answer text.');

    case 'rel_tag':
      return field.multi
        ? renderRelTagMultiSelect(values)
        : renderRelTagSingleSelect(values);

    case 'enum_boolean':
      return renderBooleanValue();

    case 'date':
      return renderDateValue();

    case 'rel_file':
      return renderFileUpload();

    case 'number':
      return renderTagValue('42');

    case 'url':
      return renderTagValue('https://example.com');

    default:
      return renderTagValue('Sample answer');
  }
};

const FieldItem: React.FC<{ field: any }> = ({ field }) => {
  const { label, description, kind, required = false } = field;

  return (
    <>
      <Flex flexDir="column" gap="6px">
        <Flex gap="6px" alignItems="center">
          {kind === 'nps_rating' && (
            <KTag
              value="NPS"
              color="pastel-4-dark"
              bgColor="pastel-4-light"
              w="fit-content"
              size="xs-semi-bold"
            />
          )}
          <Flex gap="2px">
            <KText color="secondary-900" w="fit-content">
              {label}
            </KText>
            {required && <KText color="error-200">*</KText>}
          </Flex>
        </Flex>

        {description && (
          <KText size="xs-regular" color="secondary-700">
            {description}
          </KText>
        )}

        <Box>{getExampleValue(field)}</Box>
      </Flex>
      <Divider
        orientation="horizontal"
        borderColor="border-color"
        borderWidth="1px"
      />
    </>
  );
};

export const SurveyPreviewWithAnswers: React.FC<
  SurveyPreviewWithAnswersProps
> = ({ customFields = [], fieldGroups = {} }) => {
  // Group questions by field_group_idx
  const groupedQuestions = customFields.reduce(
    (acc, field) => {
      const groupIdx = field.field_group_idx || 1;
      if (!acc[groupIdx]) {
        acc[groupIdx] = [];
      }
      acc[groupIdx].push(field);
      return acc;
    },
    {} as Record<number, any[]>
  );

  // Get section name from field_groups
  const getSectionName = (groupIdx: number) => {
    return fieldGroups[groupIdx] || `Section ${groupIdx}`;
  };

  // Sort group indices
  const sortedGroupIndices = Object.keys(groupedQuestions)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <Flex flexDir="column" gap="24px" px="16px" pb="20px">
      {sortedGroupIndices.map((groupIdx, _sectionIndex) => {
        const sectionQuestions = groupedQuestions[groupIdx].sort(
          (a, b) => a.position - b.position
        );
        const sectionName = getSectionName(groupIdx);

        // Check if section contains NPS questions
        const hasNPSQuestion = sectionQuestions?.some(
          (q) => q.kind === 'nps_rating'
        );

        return (
          <Box key={groupIdx}>
            {/* Section Header */}
            <Flex alignItems="center" gap="12px" mb="16px">
              <Flex
                alignItems="center"
                gap="6px"
                px="8px"
                py="6px"
                borderRadius="6px"
                backgroundColor={colors['secondary-50']}
              >
                <TruncatedTextWithTooltip
                  color={'secondary-900'}
                  size="sm-medium"
                  text={sectionName}
                  enableTooltip={true}
                  maxWidth="250px"
                />
                {/* Hide count for NPS sections */}
                {!hasNPSQuestion && (
                  <Flex
                    alignItems="center"
                    bg="base-white"
                    px="6px"
                    borderRadius="6px"
                    border="1px solid"
                    borderColor="border-color"
                  >
                    <KText as="span" size="sm-semi-bold" color="secondary-700">
                      {sectionQuestions?.length}
                    </KText>
                  </Flex>
                )}
              </Flex>
              <Box
                borderBottom="1px solid"
                borderColor="border-color"
                flex={1}
              />
            </Flex>

            {/* Section Questions */}
            <Flex flexDir="column" gap="16px">
              {sectionQuestions.map((field) => (
                <FieldItem key={field.id} field={field} />
              ))}
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
};
